import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { CalendarCheck, MessageSquare, AlertCircle, CheckCircle2 } from "lucide-react";

export default function AdminDashboard() {
  const { authAxios } = useAdminAuth();
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [b, m] = await Promise.all([
          authAxios.get("/bookings"),
          authAxios.get("/messages"),
        ]);
        setBookings(b.data || []);
        setMessages(m.data || []);
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = [
    { label: "Total bookings", value: bookings.length, icon: CalendarCheck, color: "sky" },
    {
      label: "Confirmed bookings",
      value: bookings.filter((b) => b.status === "customer_confirmed" || b.status === "confirmed").length,
      icon: CheckCircle2,
      color: "emerald",
    },
    { label: "Total enquiries", value: messages.length, icon: MessageSquare, color: "indigo" },
    {
      label: "Unread enquiries",
      value: messages.filter((m) => m.status === "new").length,
      icon: AlertCircle,
      color: "amber",
    },
  ];

  return (
    <div className="p-8">
      <h1 className="text-2xl font-extrabold text-slate-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{s.label}</span>
              <s.icon size={18} className={`text-${s.color}-500`} />
            </div>
            <div className="text-3xl font-extrabold text-slate-900">{loading ? "…" : s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <RecentList title="Recent bookings" items={bookings.slice(0, 5)} type="booking" />
        <RecentList title="Recent enquiries" items={messages.slice(0, 5)} type="message" />
      </div>
    </div>
  );
}

function RecentList({ title, items, type }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-3 border-b border-slate-100 font-bold text-slate-800">{title}</div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">Nothing here yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="px-5 py-3 flex items-center justify-between text-sm">
            <div className="truncate">
              <div className="font-semibold text-slate-800">{it.name}</div>
              <div className="text-xs text-slate-500 truncate">
                {type === "booking" ? `${it.service_title} • ${it.date} ${it.time}` : it.service_title}
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{it.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
